import {
    FundParsePositionMappingChecks,
    FundParsePositionMappingRow,
    FundParsePositionMappingSummary
} from "./fundParseTypes";
import { FundParsePositionRecord, TptRow } from "./fundParseTptTypes";
import { normalizeIdentifier } from "./fundParseReportPayload";

const PORTFOLIO_ID_KEY = "1_Portfolio_identifying_data";
const PORTFOLIO_NAME_KEY = "3_Portfolio_name";
const CURRENCY_KEY = "4_Portfolio_currency_(B)";
const NAV_KEY = "5_Net_asset_valuation_of_the_portfolio_or_the_share_class_in_portfolio_currency";
const MARKET_VALUE_KEY = "24_Market_valuation_in_portfolio_currency_(B)";

const RELATIVE_TOLERANCE = 1e-4;

function toFloat(value: unknown): number | null {
    if (value === null || value === "" || typeof value === "undefined") {
        return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return null;
    }
    return parsed;
}

function relativeDifference(numerator: number, denominator: number): number {
    if (denominator === 0) {
        return Math.abs(numerator);
    }
    return Math.abs(numerator - denominator) / Math.abs(denominator);
}

function sumMarketValues(rows: TptRow[]): number {
    let total = 0;
    for (const row of rows) {
        const value = toFloat(row[MARKET_VALUE_KEY]);
        if (value !== null) {
            total += value;
        }
    }
    return total;
}

function deriveMarketValueTotals(
    marketValueTotal: number,
    positionScaling: number | null,
    tptMarketValuesAreScaled: boolean
): [number, number] {
    if (tptMarketValuesAreScaled) {
        const scaledTotal = marketValueTotal;
        if (positionScaling !== null && positionScaling !== 0) {
            return [scaledTotal / positionScaling, scaledTotal];
        }
        return [0, scaledTotal];
    }
    const unscaledTotal = marketValueTotal;
    const scaledTotal = positionScaling !== null ? unscaledTotal * positionScaling : 0;
    return [unscaledTotal, scaledTotal];
}

function portfolioMetadataByIsin(combinedData: TptRow[]): Record<string, Record<string, unknown>> {
    const metadata: Record<string, Record<string, unknown>> = {};
    for (const row of combinedData) {
        const isin = normalizeIdentifier(row[PORTFOLIO_ID_KEY]);
        if (!isin || metadata[isin]) {
            continue;
        }
        metadata[isin] = {
            [CURRENCY_KEY]: row[CURRENCY_KEY],
            [NAV_KEY]: row[NAV_KEY]
        };
    }
    return metadata;
}

function tptRowsByIsin(combinedData: TptRow[]): Record<string, TptRow[]> {
    const grouped: Record<string, TptRow[]> = {};
    for (const row of combinedData) {
        const isin = normalizeIdentifier(row[PORTFOLIO_ID_KEY]);
        if (!isin) {
            continue;
        }
        if (!grouped[isin]) {
            grouped[isin] = [];
        }
        grouped[isin].push(row);
    }
    return grouped;
}

function firstNonEmpty(values: unknown[]): string {
    for (const value of values) {
        const text = String(value ?? "").trim();
        if (text) {
            return text;
        }
    }
    return "";
}

function buildChecks(
    clientValuation: number,
    tptNav: number | null,
    positionScaling: number | null,
    unscaledTotal: number,
    scaledTotal: number,
    coverageRatio: number | null,
    reconciliationRatio: number | null
): FundParsePositionMappingChecks {
    const hasTptNav = tptNav !== null && tptNav !== 0;
    const hasTptMarketValues = unscaledTotal !== 0;

    let scalingIdentityOk = false;
    if (hasTptNav && positionScaling !== null) {
        scalingIdentityOk = relativeDifference(positionScaling * tptNav, clientValuation) <= RELATIVE_TOLERANCE;
    }

    let scalingAppliedOk = false;
    if (coverageRatio !== null && reconciliationRatio !== null) {
        scalingAppliedOk = relativeDifference(reconciliationRatio, coverageRatio) <= RELATIVE_TOLERANCE;
    }

    return {
        scalingIdentityOk,
        scalingAppliedOk,
        hasTptNav,
        hasTptMarketValues
    };
}

export function buildPositionMappingSummary(
    combinedData: TptRow[],
    posRecords: FundParsePositionRecord[],
    valuationDate: string,
    tptMarketValuesAreScaled = true
): FundParsePositionMappingSummary {
    const rows: FundParsePositionMappingRow[] = [];
    if (!posRecords.length) {
        return { valuationDate, rows };
    }

    const portfolioNav = portfolioMetadataByIsin(combinedData);
    const tptByIsin = tptRowsByIsin(combinedData);
    const matchedIsins = Object.keys(tptByIsin).sort();

    for (const isin of matchedIsins) {
        const clientRows = posRecords.filter((record) => normalizeIdentifier(record.ISIN) === isin);
        if (!clientRows.length) {
            continue;
        }

        const fundRows = tptByIsin[isin];
        const clientQuantity = clientRows.reduce((sum, row) => sum + row["Quantity Held"], 0);
        const clientValuation = clientRows.reduce((sum, row) => sum + row.Valuation, 0);
        const shareclassNames = Array.from(
            new Set(clientRows.map((row) => row["Fund Shareclass Name"]).filter((name) => String(name).trim()))
        ).sort().join(", ");
        const fundName =
            firstNonEmpty(clientRows.map((row) => row["Fund Name"])) ||
            firstNonEmpty(fundRows.map((row) => row[PORTFOLIO_NAME_KEY]));
        const cic = firstNonEmpty(clientRows.map((row) => row.CIC));

        const metadata = portfolioNav[isin] || {};
        const tptNav = toFloat(metadata[NAV_KEY]);
        const tptCurrency = String(metadata[CURRENCY_KEY] ?? "").trim();
        const marketValueTotal = sumMarketValues(fundRows);

        let positionScaling: number | null = null;
        if (tptNav !== null && tptNav !== 0) {
            positionScaling = clientValuation / tptNav;
        }

        const [unscaledTotal, scaledTotal] = deriveMarketValueTotals(
            marketValueTotal,
            positionScaling,
            tptMarketValuesAreScaled
        );
        const coverageRatio = tptNav !== null && tptNav !== 0 ? unscaledTotal / tptNav : null;
        const reconciliationRatio = clientValuation !== 0 ? scaledTotal / clientValuation : null;

        rows.push({
            isin,
            fundName,
            shareclassName: shareclassNames,
            cic,
            clientQuantity,
            clientValuation,
            tptNav,
            tptCurrency,
            positionScaling,
            unscaledTptMarketValueSum: unscaledTotal,
            scaledTptMarketValueSum: scaledTotal,
            coverageRatio,
            reconciliationRatio,
            matched: true,
            checks: buildChecks(
                clientValuation,
                tptNav,
                positionScaling,
                unscaledTotal,
                scaledTotal,
                coverageRatio,
                reconciliationRatio
            )
        });
    }

    for (const clientRow of posRecords) {
        const isin = normalizeIdentifier(clientRow.ISIN);
        if (!isin || matchedIsins.includes(isin)) {
            continue;
        }
        rows.push({
            isin,
            fundName: String(clientRow["Fund Name"] ?? ""),
            shareclassName: String(clientRow["Fund Shareclass Name"] ?? ""),
            cic: String(clientRow.CIC ?? ""),
            clientQuantity: clientRow["Quantity Held"] ?? 0,
            clientValuation: clientRow.Valuation ?? 0,
            tptNav: null,
            tptCurrency: "",
            positionScaling: null,
            unscaledTptMarketValueSum: 0,
            scaledTptMarketValueSum: 0,
            coverageRatio: null,
            reconciliationRatio: null,
            matched: false,
            checks: {
                scalingIdentityOk: false,
                scalingAppliedOk: false,
                hasTptNav: false,
                hasTptMarketValues: false
            }
        });
    }

    rows.sort((left, right) => {
        if (left.matched !== right.matched) {
            return left.matched ? -1 : 1;
        }
        return `${left.fundName}${left.isin}`.localeCompare(`${right.fundName}${right.isin}`);
    });

    return { valuationDate, rows };
}

export function enrichPositionsWithTptNav(
    posRecords: FundParsePositionRecord[],
    combinedData: TptRow[]
): FundParsePositionRecord[] {
    const portfolioNav = portfolioMetadataByIsin(combinedData);
    return posRecords.map((record) => {
        const metadata = portfolioNav[normalizeIdentifier(record.ISIN)] || {};
        const nav = toFloat(metadata[NAV_KEY]);
        const currency = metadata[CURRENCY_KEY];
        const positionScaling = nav !== null && nav !== 0 ? record.Valuation / nav : null;
        let nextCurrency = record[CURRENCY_KEY];
        if (typeof currency === "string" || typeof currency === "number") {
            nextCurrency = currency;
        }
        return {
            ...record,
            [CURRENCY_KEY]: nextCurrency,
            [NAV_KEY]: nav ?? record[NAV_KEY],
            "Position scaling": positionScaling
        };
    });
}
